import { type SubmitEvent, useState } from "react";
import { University } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Select,
  SelectItem,
  SelectContent,
  SelectValue,
  SelectTrigger,
} from "../ui/select";
import { STATE_PER_CODE } from "@/lib/states";
import { COUNTRY_OPTIONS, type CountryCode } from "@/lib/countries";
import { useCreateInstitutionMutation } from "@/lib/tanstack-query/institutions";
import type { CreateInstitutionParams, Institution } from "@/types/institution";

interface CreateInstitutionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setInstitution: (i: Institution) => void;
}

export const CreateInstitutionModal = ({
  open,
  onOpenChange,
  setInstitution,
}: CreateInstitutionModalProps) => {
  const { mutateAsync, isPending, error } = useCreateInstitutionMutation();
  const [country, setCountry] = useState<CountryCode>("US");

  const handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    const form = document.querySelector<HTMLFormElement>("#create-institution");
    if (!form) return;
    const formData = new FormData(form);

    const name = (formData.get("name") as string).trim();
    if (!name) {
      toast.error("You must provide a valid name.");
      return;
    }

    const websiteInput = (formData.get("website") as string).trim();
    let website: string;
    try {
      const url = websiteInput.includes("://")
        ? new URL(websiteInput)
        : new URL(`https://${websiteInput}`);
      url.protocol = "https:";
      website = url.href;
    } catch {
      toast.error("Please enter a valid website URL.");
      return;
    }

    const state =
      country === "US"
        ? (formData.get("state") as CreateInstitutionParams["state"])
        : null;

    if (country === "US" && !state) {
      toast.error("State is required for US institutions.");
      return;
    }

    try {
      const newInstitution = await mutateAsync({
        name,
        website,
        country,
        state,
      });

      setInstitution(newInstitution);
      toast.success(`Successfully created ${newInstitution.name}!`);
      onOpenChange(false);
    } catch (error) {
      toast.error(
        // @ts-expect-error todo: fix later
        error.response?.data?.message || "Failed to create institution",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <University className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle>Create Institution</DialogTitle>
              <DialogDescription className="hidden">
                Fill your institution's name, website, country, and state.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {error && (
          <div className="mt-4 p-3 text-sm text-destructive bg-destructive/10 border border-destructive rounded-md">
            Failed to create the institution:
            <br />
            {error?.message}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          id="create-institution"
          className="space-y-6"
        >
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Harvard"
              required
              disabled={isPending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="website" className="text-sm font-medium">
              Website <span className="text-red-500">*</span>
            </Label>
            <Input
              id="website"
              name="website"
              type="text"
              placeholder="harvard.edu"
              required
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">
              Country <span className="text-red-500">*</span>
            </Label>
            <Select
              value={country}
              onValueChange={(v) => setCountry(v as CountryCode)}
              disabled={isPending}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a country..." />
              </SelectTrigger>
              <SelectContent>
                {COUNTRY_OPTIONS.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {country === "US" && (
            <div className="space-y-2">
              <Label htmlFor="state" className="text-sm font-medium">
                State <span className="text-red-500">*</span>
              </Label>
              <Select name="state" required disabled={isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a state..." />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATE_PER_CODE).map(([code, name]) => (
                    <SelectItem key={code} value={code}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter className="border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="min-w-[120px]"
            >
              {isPending ? (
                <>
                  <span className="mr-2">Creating...</span>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </>
              ) : (
                "Create Institution"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
